import type { Middleware } from "@reduxjs/toolkit";
import type { QueryClient } from "@tanstack/react-query";
import { type SessionState, sessionSlice } from "./session";

interface SessionCacheRootState {
  session: SessionState;
}

export const hasSession = (session: SessionState): boolean =>
  Boolean(session.user || session.accessToken);

const sessionIdentity = (session: SessionState): string | null =>
  session.user ? `${session.user.accountId}:${session.user.id}` : null;

export const sessionIdentityChanged = (
  previousSession: SessionState,
  nextSession: SessionState,
): boolean => {
  const previousIdentity = sessionIdentity(previousSession);
  const nextIdentity = sessionIdentity(nextSession);

  if (previousIdentity !== nextIdentity) {
    return true;
  }

  const previousHadToken = Boolean(previousSession.accessToken);
  const nextHasToken = Boolean(nextSession.accessToken);
  if (previousHadToken !== nextHasToken) {
    return true;
  }

  // A token without a user is an incomplete session, so there is no stable
  // identity to compare. Clear conservatively if another tab replaces it.
  return (
    previousIdentity === null &&
    previousSession.accessToken !== nextSession.accessToken
  );
};

/**
 * Clear all cached queries synchronously at authentication boundaries. Keeping
 * this in Redux middleware covers hook-driven logout, router-driven logout,
 * and cross-tab storage sync before React can render data from the prior
 * session. Query removal deliberately leaves mutation state alone because
 * login is dispatched from mutation `onSuccess` handlers. Silent same-user
 * token refreshes use `tokenRefreshed` and bypass this cleanup.
 */
export const createSessionCacheMiddleware =
  (queryClient: QueryClient): Middleware<unknown, SessionCacheRootState> =>
  ({ getState }) =>
  (next) =>
  (action) => {
    const previousSession = getState().session;
    const result = next(action);
    const nextSession = getState().session;

    const shouldClear =
      sessionSlice.actions.login.match(action) ||
      (sessionSlice.actions.logout.match(action) &&
        hasSession(previousSession)) ||
      (sessionSlice.actions.syncAuthFromStorage.match(action) &&
        sessionIdentityChanged(previousSession, nextSession));

    if (shouldClear) {
      queryClient.removeQueries();
    }

    return result;
  };
